import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Types
export interface Student {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
  surname?: string;
}

export interface Transaction {
  id: number;
  amount: string;
  paymentDate: string;
  bankReferenceNumber: string;
  fullName: string;
  status: 'paid' | 'returned';
}

export interface Certificate {
  id: number;
  certNumber: number;
  filename: string;
  serviceType: 'Индивидуальное обучение' | 'Групповые занятия';
  lessonsCount: number;
  pricePerLesson: string;
  servicePeriodFrom: string;
  servicePeriodTo: string;
  createdAt: string;
  student: Student;
  transaction: Transaction;
  creator: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

export interface CreateCertificateData {
  studentId: number;
  serviceType: 'Индивидуальное обучение' | 'Групповые занятия';
  lessonsCount: number;
  pricePerLesson: number;
  servicePeriodFrom: string;
  servicePeriodTo: string;
  paymentDate: string;
  bankReferenceNumber: string;
}

export interface CertificatesFilters {
  search: string;
  serviceType: string;
  dateFrom: string;
  dateTo: string;
}

export interface CertificatesPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface CertificatesState {
  certificates: Certificate[];
  studentsForDropdown: Student[];
  loading: boolean;
  error: string | null;
  filters: CertificatesFilters;
  pagination: CertificatesPagination;
  createModalOpen: boolean;
}

// Initial state
const initialState: CertificatesState = {
  certificates: [],
  studentsForDropdown: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    serviceType: '',
    dateFrom: '',
    dateTo: ''
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  },
  createModalOpen: false
};

// Async thunks
export const fetchCertificatesAsync = createAsyncThunk(
  'certificates/fetchCertificates',
  async (params: { page?: number; limit?: number; search?: string; serviceType?: string; dateFrom?: string; dateTo?: string }, { rejectWithValue, getState }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.serviceType) queryParams.append('serviceType', params.serviceType);
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);

      const response = await axios.get(`${API_BASE_URL}/certificates?${queryParams.toString()}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch certificates'));
    }
  }
);

export const fetchStudentsForDropdownAsync = createAsyncThunk(
  'certificates/fetchStudentsForDropdown',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/certificates/students`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.students;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch students'));
    }
  }
);

export const createCertificateAsync = createAsyncThunk(
  'certificates/createCertificate',
  async (certificateData: CreateCertificateData, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/certificates`, certificateData, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create certificate'));
    }
  }
);

export const downloadCertificateAsync = createAsyncThunk(
  'certificates/downloadCertificate',
  async (certificateId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/certificates/${certificateId}/download`, {
        responseType: 'blob',
        headers: getAuthHeaders(getState)
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `certificate_${certificateId}.pdf`;
      
      if (contentDisposition) {
        // Try multiple patterns to extract filename
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
        }
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        }
        
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { certificateId, filename };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to download certificate'));
    }
  }
);

export const fetchCertificateByIdAsync = createAsyncThunk(
  'certificates/fetchCertificateById',
  async (certificateId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/certificates/${certificateId}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.certificate;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch certificate'));
    }
  }
);

// Slice
const certificatesSlice = createSlice({
  name: 'certificates',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<CertificatesFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        serviceType: '',
        dateFrom: '',
        dateTo: ''
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      if (!state.pagination) {
        state.pagination = {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 20
        };
      }
      state.pagination.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    openCreateModal: (state) => {
      state.createModalOpen = true;
    },
    closeCreateModal: (state) => {
      state.createModalOpen = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch certificates
      .addCase(fetchCertificatesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificatesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.certificates = action.payload.certificates;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCertificatesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch certificates';
      })
      
      // Fetch students for dropdown
      .addCase(fetchStudentsForDropdownAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStudentsForDropdownAsync.fulfilled, (state, action) => {
        state.studentsForDropdown = action.payload;
      })
      .addCase(fetchStudentsForDropdownAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch students';
      })
      
      // Create certificate
      .addCase(createCertificateAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCertificateAsync.fulfilled, (state, _action) => {
        state.loading = false;
        state.createModalOpen = false;
        // Optionally add the new certificate to the list or refetch
      })
      .addCase(createCertificateAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create certificate';
      })
      
      // Download certificate
      .addCase(downloadCertificateAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(downloadCertificateAsync.fulfilled, (_state) => {
        // Download completed successfully
      })
      .addCase(downloadCertificateAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to download certificate';
      })
      
      // Fetch certificate by ID
      .addCase(fetchCertificateByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificateByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Update the certificate in the list if it exists
        const index = state.certificates.findIndex(cert => cert.id === action.payload.id);
        if (index !== -1) {
          state.certificates[index] = action.payload;
        }
      })
      .addCase(fetchCertificateByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch certificate details';
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setPage,
  clearError,
  openCreateModal,
  closeCreateModal
} = certificatesSlice.actions;

export default certificatesSlice.reducer; 